import { ApiError, api } from '../../lib/api'
import { download } from '../../lib/download'
import { isEnglish } from '../../lib/i18n'

/**
 * Programme reports — `Features/Admin/LearningProgramsReports/Endpoint.cs`,
 * `ContentManagers`.
 *
 *   getReportsPage    -> GET  /api/admin/programs-reports/         the programme list
 *   getProgramSummary -> POST /LearningProgramsReports/GetProgramDetails  { Id, From, To }
 *   listReportRows    -> POST /LearningProgramsReports/{GetSucceeded, GetFailed,
 *                             GetUncompleted, GetUnattended, GetGroupsLevel}
 *
 * The five drill-downs read their programme from server session state the legacy page set
 * and nothing here did, so each answered 500. They take `ProgramId` in the body now, like
 * `GetProgramDetails`.
 */

const BASE = '/LearningProgramsReports'

const label = (arabic, english) => (isEnglish ? english : arabic) || arabic || english || ''

/** The drill-down tabs, in the order the shares are shown. */
export const REPORT_ROW_KINDS = ['success', 'failure', 'incomplete', 'absent', 'groups']

const ROW_PATHS = {
  success: 'GetSucceeded',
  failure: 'GetFailed',
  incomplete: 'GetUncompleted',
  absent: 'GetUnattended',
  groups: 'GetGroupsLevel',
}

/**
 * One page of a drill-down. The rows are passed through as the server sends them — the
 * percentages on the department rows are already formatted for the request's language.
 */
export async function listReportRows({ kind, programId, fromDate, toDate, page = 1, pageSize = 10, search = '', signal } = {}) {
  const response = await api.post(
    `${BASE}/${ROW_PATHS[kind]}`,
    {
      ProgramId: Number(programId),
      Draw: 1,
      Start: (Math.max(page, 1) - 1) * pageSize,
      Length: pageSize,
      SearchValue: search || null,
      From: fromDate || null,
      To: toDate || null,
    },
    { signal },
  )
  return {
    rows: response?.data ?? response?.Data ?? [],
    total: response?.recordsFiltered ?? response?.RecordsFiltered ?? 0,
  }
}

/** The programmes a report can be run for. */
export async function getReportsPage(signal) {
  const dto = (await api.get('/api/admin/programs-reports/', { signal })) ?? {}

  return {
    programs: (dto.Programs ?? []).map((program) => ({
      value: String(program.Id),
      label: label(program.NameArabic, program.NameEnglish) || program.Name || '',
    })),
  }
}

/**
 * One programme's outcome, optionally within a date range.
 *
 * These four percentages are the same figures the programmes list draws its dials from —
 * per programme, which is the only granularity any endpoint offers. Nothing aggregates them
 * across programmes, which is why that strip stays hidden (`COSMETIC_UI.md`).
 */
export async function getProgramSummary({ programId, fromDate, toDate, signal } = {}) {
  const response = await api.post(
    `${BASE}/GetProgramDetails`,
    { Id: Number(programId), From: fromDate || null, To: toDate || null },
    { signal },
  )

  // The endpoint wraps its anonymous report payload in DataSet2.
  const dto = response?.DataSet2 ?? response
  if (!dto) return null

  return {
    title: dto.ProgramTitle ?? '',
    startDate: dto.ProgramStartDate ?? '',
    endDate: dto.ProgramEndDate ?? '',
    coursesCount: dto.CoursesCount ?? 0,
    groupsCount: dto.GroupsCount ?? 0,
    usersCount: dto.UsersCount ?? 0,
    outcome: {
      success: Number(dto.SuccessPercentage ?? 0),
      failure: Number(dto.FailurePercentage ?? 0),
      incomplete: Number(dto.UncompletedPercentage ?? 0),
      absent: Number(dto.UnattendedPercentage ?? 0),
    },
    counts: {
      success: dto.SuccessCount ?? 0,
      failure: dto.FailureCount ?? 0,
      incomplete: dto.UncompletedCount ?? 0,
      absent: dto.UnattendedCount ?? 0,
    },
    courses: (dto.coursesData ?? dto.CoursesData ?? []).map((course) => ({
      id: course._7 ?? course.Id,
      name: course._0 ?? course.Name ?? '',
      startDate: course._1 ?? course.StartDate ?? '',
      endDate: course._2 ?? course.EndDate ?? '',
      topicsCount: Number(course._3 ?? course.CategoriesCount ?? 0),
      success: Number(course._4 ?? course.SuccessCount ?? 0),
      failure: Number(course._5 ?? course.FailureCount ?? 0),
      absent: Number(course._6 ?? course.AbsenceCount ?? 0),
    })),
    /* One row per (question, answer) with how many chose it — not a per-user list. */
    survey: (dto.surveyData ?? dto.SurveyData ?? []).map((row, index) => ({
      id: `${row._0 ?? row.Question}|${row._1 ?? row.Answer}|${index}`,
      question: row._0 ?? row.Question ?? '',
      answer: row._1 ?? row.Answer ?? '',
      count: Number(row._2 ?? row.Count ?? 0),
    })),
  }
}

/** Kept for the caller's error handling; the summary is the only call that can fail here. */
export async function tryGetProgramSummary(args) {
  try {
    return { ok: true, summary: await getProgramSummary(args) }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message, summary: null }
    throw error
  }
}

/**
 * Downloads the full program report as an Excel (.xlsx) workbook.
 */
export async function exportProgramReport({ programId, fromDate, toDate } = {}) {
  const query = new URLSearchParams()
  query.set('programId', String(programId))
  if (fromDate) query.set('from', fromDate)
  if (toDate) query.set('to', toDate)

  return download(`/api/admin/programs-reports/export?${query.toString()}`)
}
